import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Shield, ShieldAlert, Ban, Unlock, ChevronUp, ChevronDown, Bell } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGE = {
  master_admin: "bg-red-500 text-black",
  admin: "bg-orange-500 text-black",
  user: "bg-gray-600 text-white",
};
const STATUS_BADGE = {
  active: "bg-green-600 text-white",
  blocked: "bg-yellow-500 text-black",
  banned: "bg-red-700 text-white",
};

const UserRoster = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [pendingAppeals, setPendingAppeals] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [blockReason, setBlockReason] = useState("");
  const [blockTarget, setBlockTarget] = useState(null);
  const [banTarget, setBanTarget] = useState(null);
  const [banConfirmText, setBanConfirmText] = useState("");
  const [showAppeals, setShowAppeals] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, appealsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/appeals`),
      ]);
      setUsers(usersRes.data.users || []);
      setPendingAppeals(usersRes.data.pending_appeals || 0);
      setAppeals(appealsRes.data || []);
    } catch {
      toast.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    let list = users;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        (u.callsign || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q)
      );
    }
    if (filter === "admins") list = list.filter(u => u.role === "admin" || u.role === "master_admin");
    else if (filter === "blocked") list = list.filter(u => u.status === "blocked");
    else if (filter === "banned") list = list.filter(u => u.status === "banned");
    return list;
  }, [users, search, filter]);

  const isMaster = currentUser?.role === "master_admin";
  const pendingAppealsList = useMemo(() => appeals.filter(a => a.status === "pending"), [appeals]);

  const handlePromote = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/promote`);
      toast.success("User promoted to admin");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDemote = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/demote`);
      toast.success("Admin privileges removed");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleBlock = async () => {
    if (!blockTarget || !blockReason.trim()) return;
    try {
      await axios.post(`${API}/admin/users/${blockTarget}/block`, { reason: blockReason.trim() });
      toast.success("User blocked");
      setBlockTarget(null);
      setBlockReason("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleUnblock = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unblock`);
      toast.success("User unblocked");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleBan = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/ban`, { reason: "Banned by admin" });
      toast.success("User banned permanently");
      setBanTarget(null);
      setBanConfirmText("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleResolveAppeal = async (appealId, action) => {
    try {
      await axios.post(`${API}/admin/appeals/${appealId}/resolve`, { action });
      toast.success(action === "unblock" ? "User unblocked" : "Appeal dismissed");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <p className="text-sm text-gray-400">Loading users...</p>;

  return (
    <div className="space-y-6" data-testid="user-roster">
      {/* Appeals notification */}
      {pendingAppeals > 0 && (
        <button
          onClick={() => setShowAppeals(!showAppeals)}
          className="w-full flex items-center justify-between border border-yellow-500/50 bg-yellow-500/10 px-4 py-3 text-left"
          data-testid="appeals-banner"
        >
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-yellow-400" />
            <span className="text-sm font-data text-yellow-400">
              {pendingAppeals} pending unblock appeal{pendingAppeals > 1 ? "s" : ""}
            </span>
          </div>
          {showAppeals ? <ChevronUp className="h-4 w-4 text-yellow-400" /> : <ChevronDown className="h-4 w-4 text-yellow-400" />}
        </button>
      )}

      {/* Appeals panel */}
      {showAppeals && pendingAppealsList.length > 0 && (
        <div className="border border-yellow-500/30 p-4 space-y-3" data-testid="appeals-panel">
          <h3 className="font-headings text-lg uppercase tracking-widest text-yellow-400">Unblock Appeals</h3>
          {pendingAppealsList.map(appeal => (
            <div key={appeal.appeal_id} className="border border-white/10 p-3 space-y-2" data-testid={`appeal-${appeal.appeal_id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-data text-white">{appeal.callsign || appeal.email}</span>
                <span className="text-xs text-gray-500">{new Date(appeal.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-xs text-gray-400">{appeal.message}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleResolveAppeal(appeal.appeal_id, "unblock")}
                  className="border border-green-500/40 px-3 py-1 text-xs text-green-400 hover:border-green-500"
                  data-testid={`appeal-unblock-${appeal.appeal_id}`}
                >
                  Unblock
                </button>
                <button
                  onClick={() => handleResolveAppeal(appeal.appeal_id, "dismiss")}
                  className="border border-red-500/40 px-3 py-1 text-xs text-red-400 hover:border-red-500"
                  data-testid={`appeal-dismiss-${appeal.appeal_id}`}
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Block modal */}
      {blockTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="w-full max-w-md mx-4 hud-card clip-corners p-6 border border-yellow-500/50" data-testid="block-modal">
            <h3 className="font-headings text-xl uppercase tracking-widest text-yellow-400 mb-4">Block User</h3>
            <p className="text-sm text-gray-400 mb-3">This user will be unable to log in until unblocked. Provide a reason (visible to the user):</p>
            <textarea
              value={blockReason}
              onChange={e => setBlockReason(e.target.value)}
              placeholder="Reason for block..."
              className="w-full bg-black/50 border border-white/20 text-white font-data text-sm py-2 px-3 h-24 focus:border-yellow-500 outline-none resize-none"
              data-testid="block-reason-input"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={handleBlock} disabled={!blockReason.trim()} className="border border-yellow-500 px-4 py-2 text-xs text-yellow-400 hover:bg-yellow-500/10 disabled:opacity-40" data-testid="block-confirm">Confirm Block</button>
              <button onClick={() => { setBlockTarget(null); setBlockReason(""); }} className="border border-white/10 px-4 py-2 text-xs text-gray-400" data-testid="block-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Ban confirm modal */}
      {banTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="w-full max-w-md mx-4 hud-card clip-corners p-6 border border-red-500/50" data-testid="ban-modal">
            <h3 className="font-headings text-xl uppercase tracking-widest text-red-400 mb-4">Permanently Ban User</h3>
            <p className="text-sm text-gray-400 mb-2">
              Banning <span className="text-white font-data">{banTarget.callsign || banTarget.email}</span> will:
            </p>
            <ul className="text-xs text-gray-500 list-disc list-inside mb-4 space-y-1">
              <li>Blacklist their email (cannot register again)</li>
              <li>Wipe all practice stats and leaderboard entries</li>
              <li>Immediately terminate their session</li>
            </ul>
            <p className="text-sm text-red-400 mb-2">Type <span className="font-data">BAN</span> to confirm:</p>
            <input
              value={banConfirmText}
              onChange={e => setBanConfirmText(e.target.value)}
              placeholder="Type BAN"
              className="w-full bg-black/50 border border-red-500/30 text-white font-data text-sm py-2 px-3 focus:border-red-500 outline-none"
              data-testid="ban-confirm-input"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => handleBan(banTarget.user_id)}
                disabled={banConfirmText !== "BAN"}
                className="border border-red-500 px-4 py-2 text-xs text-red-400 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="ban-confirm-button"
              >
                Confirm Ban
              </button>
              <button
                onClick={() => { setBanTarget(null); setBanConfirmText(""); }}
                className="border border-white/10 px-4 py-2 text-xs text-gray-400"
                data-testid="ban-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by callsign, email, or name"
              className="w-full bg-black/50 border border-white/10 text-[#FFE800] font-data text-sm py-2 pl-10 pr-3 focus:border-[#FFE800] outline-none"
              data-testid="user-search"
            />
          </div>
        </div>
        {["all", "admins", "blocked", "banned"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`border px-3 py-1 text-xs font-headings uppercase tracking-widest ${filter === f ? "border-[#FFE800] text-[#FFE800]" : "border-white/10 text-gray-400 hover:text-[#FFE800]"}`}
            data-testid={`user-filter-${f}`}
          >{f} ({f === "all" ? users.length : users.filter(u => f === "admins" ? (u.role === "admin" || u.role === "master_admin") : u.status === f.slice(0, -2 === 0 ? undefined : undefined)).length})</button>
        ))}
      </div>

      {/* User list */}
      <div className="space-y-2" data-testid="user-list">
        <p className="text-xs text-gray-500">{filtered.length} user{filtered.length !== 1 ? "s" : ""}</p>
        {filtered.map(u => (
          <div key={u.user_id} className="border border-white/10 p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`user-row-${u.user_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {u.picture ? <img src={u.picture} alt="" className="h-8 w-8 rounded-full" /> : <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-400">{(u.callsign || u.name || "?")[0]}</div>}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-data text-white truncate">{u.callsign || u.name || "No callsign"}</span>
                  <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${ROLE_BADGE[u.role] || ROLE_BADGE.user}`}>{u.role?.toUpperCase()}</span>
                  <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${STATUS_BADGE[u.status] || STATUS_BADGE.active}`}>{u.status?.toUpperCase()}</span>
                </div>
                <p className="text-xs text-gray-500 truncate">{u.email} — {u.auth_method} — joined {new Date(u.created_at).toLocaleDateString()}</p>
                {u.status === "blocked" && u.block_reason && <p className="text-xs text-yellow-400 mt-1">Block reason: {u.block_reason}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {u.user_id !== currentUser?.user_id && u.role !== "master_admin" && (
                <>
                  {isMaster && u.role === "user" && u.status === "active" && (
                    <button onClick={() => handlePromote(u.user_id)} title="Promote to admin" className="border border-orange-500/40 p-1.5 text-orange-400 hover:border-orange-500" data-testid={`promote-${u.user_id}`}><ChevronUp className="h-3 w-3" /></button>
                  )}
                  {isMaster && u.role === "admin" && (
                    <button onClick={() => handleDemote(u.user_id)} title="Remove admin" className="border border-gray-500/40 p-1.5 text-gray-400 hover:border-gray-500" data-testid={`demote-${u.user_id}`}><ChevronDown className="h-3 w-3" /></button>
                  )}
                  {u.status === "active" && u.role === "user" && (
                    <button onClick={() => setBlockTarget(u.user_id)} title="Block user" className="border border-yellow-500/40 p-1.5 text-yellow-400 hover:border-yellow-500" data-testid={`block-${u.user_id}`}><ShieldAlert className="h-3 w-3" /></button>
                  )}
                  {u.status === "blocked" && (
                    <button onClick={() => handleUnblock(u.user_id)} title="Unblock user" className="border border-green-500/40 p-1.5 text-green-400 hover:border-green-500" data-testid={`unblock-${u.user_id}`}><Unlock className="h-3 w-3" /></button>
                  )}
                  {u.status !== "banned" && u.role === "user" && (
                    <button onClick={() => setBanTarget(u)} title="Ban permanently" className="border border-red-500/40 p-1.5 text-red-400 hover:border-red-500" data-testid={`ban-${u.user_id}`}><Ban className="h-3 w-3" /></button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserRoster;
